import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Lock } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import DoctorShell from "@/components/DoctorShell";
import { inr } from "@/lib/constants";

export default function DoctorPackages() {
    const nav = useNavigate();
    const [packages, setPackages] = useState([]);
    const [doctor, setDoctor] = useState(null);
    const [summary, setSummary] = useState(null);
    const [busy, setBusy] = useState("");

    useEffect(() => {
        Promise.all([
            api.get("/packages"),
            api.get("/me/doctor"),
            api.get("/payments/me/summary"),
        ])
            .then(([p, d, s]) => {
                setPackages(p.data);
                setDoctor(d.data);
                setSummary(s.data);
            })
            .catch((err) =>
                toast.error(errMsg(err, "Failed to load"))
            );
    }, []);

    const buy = async (code) => {
        setBusy(code);
        try {
            const { data } = await api.post("/payments/orders", { package_code: code });
            toast.success("Order created — proceeding to checkout");
            nav(`/doctor/checkout/${data.id}`);
        } catch (err) {
            // Graceful degradation for gateway_unavailable (Cashfree not
            // activated yet) — show the friendly message, never raw errors.
            const detail = err?.response?.data?.detail;
            if (detail && typeof detail === "object" && detail.code === "gateway_unavailable") {
                toast.error("Online payment is being activated", {
                    description: detail.message,
                    duration: 10000,
                });
            } else {
                toast.error(typeof detail === "string" ? detail : (detail?.message || "Could not create order"));
            }
        } finally {
            setBusy("");
        }
    };

    const currentTier = (() => {
        const code = summary?.package_code;
        return packages.find((p) => p.code === code)?.tier_level || 0;
    })();

    return (
        <DoctorShell doctor={doctor}>
            <div className="animate-fade-up">
                <div className="ddi-eyebrow text-[#0F766E]">
                    Packages
                </div>
                <h1 className="mt-1.5 text-3xl md:text-[34px] font-bold text-[#123B5D] tracking-tight leading-[1.1]">
                    Choose your package
                </h1>
                <p className="mt-2 text-sm text-[#17212B]/70 leading-relaxed max-w-2xl">
                    Pay 50% now to begin onboarding. The balance is due before publish. Annual AMC starts from year 2.
                </p>

                {summary?.state && summary.state !== "unpaid" ? (
                    <div className="mt-6 ddi-card ddi-shadow-sm p-4 text-sm bg-[#F5F7FB] border-[#3A80F7]/20" data-testid="packages-state-banner">
                        <span className="font-semibold text-[#123B5D]">
                            Current state:
                        </span>{" "}
                        <span className="capitalize">
                            {summary.state.replaceAll("_", " ")}
                        </span>
                        {summary.package_name ? (
                            <span> · {summary.package_name}</span>
                        ) : null}
                    </div>
                ) : null}

                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4" data-testid="packages-grid">
                    {packages.map((p) => {
                        const isCurrent = summary?.package_code === p.code;
                        const isDowngrade = summary?.package_code && p.tier_level < currentTier;
                        const isRecommended = p.code === "premium";
                        return (
                            <div
                                key={p.code}
                                className={
                                    "ddi-card p-6 ddi-lift transition-all duration-200 relative " +
                                    (isRecommended
                                        ? "border-[#0F766E] ddi-shadow-lg ring-1 ring-[#0F766E]/20"
                                        : "ddi-shadow-sm hover:ddi-shadow-md")
                                }
                                data-testid={`packages-card-${p.code}`}
                            >
                                {isRecommended && !isCurrent && (
                                    <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 z-10">
                                        <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-[#0F766E] to-[#14B8A6] text-white text-[10px] font-bold px-2.5 py-1 tracking-wider uppercase shadow-md shadow-[#0F766E]/30">
                                            <Check size={10} strokeWidth={3} /> Most popular
                                        </span>
                                    </div>
                                )}
                                {isCurrent ? (
                                    <span className="ddi-badge bg-green-100 text-[#15803D] mb-3">
                                        Current
                                    </span>
                                ) : isRecommended ? (
                                    <span className="ddi-badge bg-teal-100 text-[#0F766E] mb-3">
                                        Recommended
                                    </span>
                                ) : (
                                    <span className="ddi-badge bg-[#123B5D]/5 text-[#123B5D] mb-3">
                                        Tier {p.tier_level}
                                    </span>
                                )}
                                <div className="text-lg font-bold text-[#123B5D] tracking-tight">
                                    {p.name}
                                </div>
                                <div className="text-xs text-[#17212B]/60 mt-1 leading-relaxed">
                                    {p.tagline}
                                </div>
                                <div className="mt-5 flex items-baseline gap-1">
                                    <span className="text-[28px] font-bold text-[#17212B] tracking-tight">
                                        {inr(p.price_inr)}
                                    </span>
                                </div>
                                <div className="text-[11px] text-[#17212B]/60 mt-0.5">
                                    50% advance · + {inr(p.amc_inr)} AMC / yr
                                </div>
                                <div className="mt-5 text-sm text-[#17212B]/85 flex items-center gap-2 font-medium">
                                    <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-[#0F766E]/10">
                                        <Check size={12} className="text-[#0F766E]" strokeWidth={3} />
                                    </span>
                                    {p.feature_count} features unlocked
                                </div>
                                <button
                                    disabled={
                                        !!busy || isCurrent || isDowngrade
                                    }
                                    onClick={() => buy(p.code)}
                                    className={
                                        "mt-6 w-full text-sm font-semibold py-2.5 px-4 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed " +
                                        (isRecommended
                                            ? "bg-[#0F766E] text-white hover:bg-[#0E635B] ddi-shadow-sm"
                                            : "bg-[#123B5D] text-white hover:bg-[#0F2E4A]")
                                    }
                                    data-testid={`packages-buy-${p.code}`}
                                >
                                    {isCurrent
                                        ? "Current package"
                                        : isDowngrade
                                        ? "Not available"
                                        : busy === p.code
                                        ? "Creating order…"
                                        : `Get ${p.name}`}
                                </button>
                                {isDowngrade ? (
                                    <div className="mt-2 text-[11px] text-[#17212B]/50 flex items-center gap-1">
                                        <Lock size={11} /> Cannot switch to a
                                        lower tier
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            </div>
        </DoctorShell>
    );
}
